// src/components/Forms/CourseOrderForm/OfferLineItems.jsx
import React from "react";

function getOneTimePrice(offer) {
  if (offer?.price_type === "free") return 0;
  const raw = offer?.discounted_price ?? offer?.amount ?? offer?.price ?? "0";
  return Number.parseFloat(String(raw).trim()) || 0;
}

function getPlanInstallmentPrice(offer) {
  const raw = offer?.payment_plan_amount ?? offer?.payment_plan_price ?? "0";
  return Number.parseFloat(String(raw).trim()) || 0;
}

export default function OfferLineItems({
  offers = [],
  isMultiMode = false,
  selectedOfferIds = new Set(),
  selectedOfferId,
  selectedPricingType = "one_time",
  onSelectOption,
  onToggleOffer,
  showProducts = true,
  appliedCoupon = "",
  discountedAmount = null,
  totalPrice = 0,
}) {
  return (
    <div className="osof-table">
      <table className="table mb-0 align-middle">
        <thead>
          <tr>
            <th className="fw-semibold">Item</th>
            <th className="text-end fw-semibold" style={{ width: 80 }}>Qty</th>
            <th className="text-end fw-semibold" style={{ width: 110 }}>Price</th>
          </tr>
        </thead>
        <tbody>
          {offers.map((offer) => {
            const hasBoth = offer.payment_mode === "both";
            const isFree = offer.price_type === "free";
            const oneTime = getOneTimePrice(offer);
            const planPrice = getPlanInstallmentPrice(offer);
            const planInterval = offer.payment_plan_interval || offer.payment_plan_frequency || "month";
            const planCount = offer.payment_plan_count || offer.payment_plan_total_payments || 1;

            // If offer has both modes, render options
            const options = [];
            if (isFree) {
              options.push({
                type: "free",
                label: offer.title,
                subtext: "100% Free Access",
                price: 0,
                pricingType: "one_time",
              });
            } else if (hasBoth) {
              options.push({
                type: "one_time",
                label: `${offer.title} (Pay in Full)`,
                subtext: null,
                price: oneTime,
                pricingType: "one_time",
              });
              options.push({
                type: "payment_plan",
                label: `${offer.title} (Payment Plan)`,
                subtext: `$${planPrice.toFixed(2)} per ${planInterval} × ${planCount}`,
                price: planPrice,
                pricingType: "payment_plan",
              });
            } else if (offer.payment_mode === "payment_plan") {
              options.push({
                type: "payment_plan",
                label: offer.title,
                subtext: `$${planPrice.toFixed(2)} per ${planInterval} × ${planCount}`,
                price: planPrice,
                pricingType: "payment_plan",
              });
            } else {
              options.push({
                type: "one_time",
                label: offer.title,
                subtext: null,
                price: oneTime,
                pricingType: "one_time",
              });
            }

            return options.map((opt, optIndex) => {
              const isChecked = isMultiMode
                ? selectedOfferIds.has(offer.id)
                : selectedOfferId === offer.id &&
                  selectedPricingType === opt.pricingType;

              const handleItemClick = () => {
                if (isMultiMode) {
                  if (onToggleOffer) onToggleOffer(offer.id, opt.pricingType);
                } else {
                  if (onSelectOption) onSelectOption(offer.id, opt.pricingType);
                }
              };

              return (
                <tr
                  key={`${offer.id}-${opt.pricingType}-${optIndex}`}
                  style={{ cursor: "pointer" }}
                  onClick={handleItemClick}
                >
                  <td className="py-3">
                    <div className="form-check d-flex align-items-start">
                      <input
                        className="form-check-input mt-1 me-2"
                        type={isMultiMode ? "checkbox" : "radio"}
                        name={isMultiMode ? `offer-selection-${offer.id}` : "offer-selection"}
                        id={`opt-${offer.id}-${opt.pricingType}`}
                        checked={isChecked}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleItemClick();
                        }}
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: 18, height: 18, cursor: "pointer" }}
                      />
                      <div>
                        <label
                          className="form-check-label fw-bold text-dark d-block"
                          style={{ cursor: "pointer", fontSize: "14.5px" }}
                        >
                          {opt.label}
                        </label>
                        {opt.subtext && (
                          <div className="text-muted small mt-0.5">
                            <span className="osof-badge osof-badge-plan me-1">Plan</span>
                            {opt.subtext}
                          </div>
                        )}

                        {/* Bundled products sub-lines */}
                        {showProducts && offer.products && offer.products.length > 0 && optIndex === 0 && (
                          <ul className="osof-sublines">
                            {offer.products.map((p, pIdx) => {
                              const isCourse = p.type === "course";
                              return (
                                <li key={p.id || pIdx} className="osof-subline-item">
                                  <span className="text-muted">↳</span>
                                  <span
                                    className={`osof-badge ${
                                      isCourse ? "osof-badge-course" : "osof-badge-ebook"
                                    }`}
                                  >
                                    {isCourse ? "Course" : "Ebook"}
                                  </span>
                                  <span className="text-dark fw-medium">
                                    {p.details?.title || p.title || p.name || "Product"}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="text-end fw-bold py-3 text-muted">1</td>
                  <td className="text-end fw-bold py-3">
                    <span className="text-primary fs-6">
                      ${opt.price.toFixed(2)}
                    </span>
                  </td>
                </tr>
              );
            });
          })}
        </tbody>
        <tfoot>
          {discountedAmount !== null && (
            <tr>
              <td colSpan={2} className="fw-semibold text-success py-2">
                Coupon Discount ({appliedCoupon})
              </td>
              <td className="text-end fw-bold text-success py-2">
                -${(
                  (offers.find((o) => o.id === selectedOfferId)
                    ? getOneTimePrice(offers.find((o) => o.id === selectedOfferId))
                    : 0) - discountedAmount
                ).toFixed(2)}
              </td>
            </tr>
          )}
          <tr>
            <td colSpan={2} className="fw-bold fs-6 py-3">
              Order Total
            </td>
            <td className="text-end fw-bold fs-5 text-primary py-3">
              ${Number(totalPrice || 0).toFixed(2)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
